/**
 * ContentManager
 * Orchestrates content switching based on timeline range and current tab
 * Uses Strategy Pattern to delegate content display logic
 */

import type {
  ContentStrategy,
  ContentStrategyType,
  TimelineRange,
  TabName,
} from '@/types';
import { StorageService } from '@/services/StorageService';
import { EVENTS } from '@/utils/constants';

// Strategy imports
import { DefaultRangeStrategy } from './strategies/DefaultRangeStrategy';
import { Range1323Strategy } from './strategies/Range1323Strategy';
import { Range1925Strategy } from './strategies/Range1925Strategy';
import { Range2023Strategy } from './strategies/Range2023Strategy';
import { getCurrentTab, getCurrentPeriodType } from './strategies/ContentHelper';

/**
 * Main content manager - determines and applies appropriate content strategy
 */
export class ContentManager {
  private strategies: Map<ContentStrategyType, ContentStrategy>;
  private currentRange: TimelineRange | null = null;
  private currentTab: TabName | null = null;

  constructor(private storageService: StorageService) {
    // Initialize all strategies
    this.strategies = new Map<ContentStrategyType, ContentStrategy>([
      ['default', new DefaultRangeStrategy()],
      ['range-13-23', new Range1323Strategy()],
      ['range-19-25', new Range1925Strategy()],
      ['range-20-23', new Range2023Strategy()],
    ]);
  }

  /**
   * Initialize the content manager
   * Sets up event listeners for timeline, tab, and period type changes
   */
  public initialize(): void {
    // Listen for timeline changes
    document.addEventListener(EVENTS.TIMELINE_CHANGED, () => {
      this.updateContent();
    });

    // Listen for tab changes
    document.addEventListener(EVENTS.TAB_CHANGED, () => {
      this.updateContent();
    });

    // Listen for period type changes
    document.addEventListener(EVENTS.PERIOD_TYPE_CHANGED, () => {
      this.updateContent();
    });

    // Listen for storage events (cross-tab sync)
    window.addEventListener('storage', (e: StorageEvent) => {
      if (e.key === 'timelineRange' || e.key === 'periodType') {
        this.updateContent();
      }
    });

    // Apply initial content based on current state
    this.updateContent();
  }

  /**
   * Update content based on current timeline range, tab, and period type
   * @param timelineRange - Optional override for timeline range
   * @param currentTab - Optional override for current tab
   */
  public updateContent(
    timelineRange?: TimelineRange,
    currentTab?: TabName
  ): void {
    // Get timeline range
    const range =
      timelineRange || this.storageService.getTimelineRange() || {
        leftYear: '19',
        rightYear: '25',
      };

    // Get current tab
    const tab = currentTab || getCurrentTab();

    // Get current period type
    const periodType = getCurrentPeriodType();

    // Update internal state
    this.currentRange = range;
    this.currentTab = tab;

    // Determine which strategy to use
    const strategyType = this.determineStrategy(range);

    // Get and apply the strategy
    const strategy = this.getStrategy(strategyType);
    strategy.apply(tab, periodType);
  }

  /**
   * Determine which strategy to use based on timeline range
   */
  private determineStrategy(range: TimelineRange): ContentStrategyType {
    const { leftYear, rightYear } = range;

    // Check for specific ranges
    if (leftYear === '19' && rightYear === '25') {
      return 'range-19-25';
    }

    if (leftYear === '20' && rightYear === '23') {
      return 'range-20-23';
    }

    if (leftYear === '13' && rightYear === '23') {
      return 'range-13-23';
    }

    // Default for all other ranges
    return 'default';
  }

  /**
   * Get a strategy instance by type
   */
  private getStrategy(type: ContentStrategyType): ContentStrategy {
    const strategy = this.strategies.get(type);

    if (!strategy) {
      console.warn(
        `Strategy not found for type: ${type}, using default strategy`
      );
      return this.strategies.get('default')!;
    }

    return strategy;
  }

  /**
   * Get current state
   */
  public getCurrentState(): {
    range: TimelineRange | null;
    tab: TabName | null;
  } {
    return {
      range: this.currentRange,
      tab: this.currentTab,
    };
  }
}
